import { OutboxEntry } from 'src/app/core/offline/offline.models';
import { DocumentPinPayload, ListedDocument } from 'src/app/shared/models/document-version.model';
import { SnapshotTask } from 'src/app/tasks/offline/task-offline.models';
import { DocumentEntity, DocumentSnapshotData, DocumentUploadPayload, OfflineDocumentList, SnapshotDocument, SnapshotPin } from './document-offline.models';

// Offline E2 step 4: pure functions from the project snapshot to the Datenraum views (like report-overlay, task-overlay).

/** a snapshot pin as the document list delivers it (DocumentService.buildPinPayload), joined with its task */
export function toListedPin(pin: SnapshotPin, task: SnapshotTask | undefined): DocumentPinPayload {
  return {
    id: pin.id,
    taskId: pin.taskId,
    taskName: task?.note ?? null,
    bkp: task?.bkp ?? null,
    status: task?.status ?? null,
    journalId: task?.journalId ?? null,
    journalName: task?.journalName ?? null,
    journalType: task?.journalType ?? null,
    pageNumber: pin.pageNumber,
    x: pin.x,
    y: pin.y,
    verification: pin.verification ?? 'confirmed',
    sourcePinId: null,
    currentPin: null,
    version: pin.version ?? 1,
    // the note is too long for the snapshot (E2 gate R4): the popup says «offline nicht verfügbar» (NA-43)
    ...(task?.noteTruncated ? { noteTruncated: true } : {}),
  };
}

function toListedDocument(document: SnapshotDocument, pins: DocumentPinPayload[]): ListedDocument {
  return {
    id: document.id,
    key: document.key,
    name: document.name,
    category: document.category ?? '',
    tags: document.tags ?? [],
    description: document.description,
    external: document.external,
    bkps: document.bkps ?? [],
    version: document.version ?? 1,
    predecessorId: document.predecessorId,
    superseded: document.superseded,
    pins,
    creationDate: document.creationDate,
    pageCount: document.pageCount,
    expirationDate: document.expirationDate,
  };
}

/** current documents with their earlier versions (same walk as the backend buildVersionGroups) */
export function groupVersions(documents: ListedDocument[]): ListedDocument[] {
  const byId = new Map(documents.map((document) => [document.id, document]));
  const reached = new Set<string | undefined>();
  const groups: ListedDocument[] = [];
  for (const document of documents) {
    if (document.superseded) continue;
    const versions: ListedDocument[] = [];
    reached.add(document.id);
    let cursor = document;
    while (cursor.predecessorId && byId.has(cursor.predecessorId) && !reached.has(cursor.predecessorId)) {
      cursor = byId.get(cursor.predecessorId)!;
      reached.add(cursor.id);
      versions.push(cursor);
    }
    groups.push({ ...document, versions });
  }
  for (const document of documents) {
    if (!reached.has(document.id)) groups.push({ ...document, versions: [] });
  }
  return groups;
}

function entities(values: (string | null | undefined)[]): DocumentEntity[] {
  return [...new Set(values.filter((value): value is string => !!value))].map((name) => ({ name, canBeDeleted: false }));
}

/** documents uploaded on this device and still in the outbox (any status): cards with the badge «wartet auf Upload» */
export function localUploadDocuments(entries: OutboxEntry[], projectId: string): ListedDocument[] {
  return entries
    .filter((entry) => entry.type === 'document.upload' && entry.entityRef.projectId === projectId && !!entry.entityRef.localId)
    .map((entry) => {
      const { fields, fileName, mime } = entry.payload as unknown as DocumentUploadPayload;
      const localId = entry.entityRef.localId!;
      return {
        id: localId,
        key: localId,
        name: fields.name,
        category: fields.category ?? '',
        tags: fields.tags ?? [],
        description: fields.description,
        external: fields.external,
        bkps: fields.bkps ?? [],
        version: 1,
        superseded: false,
        pins: [],
        creationDate: new Date(entry.createdAt).toISOString(),
        mimeType: mime,
        localUpload: { opId: entry.opId, fileName, status: entry.status },
      };
    });
}

/** the Datenraum list of a project from its snapshot; pins only of visible tasks (the snapshot has no others) */
export function snapshotDocumentList(data: DocumentSnapshotData, saved: ReadonlySet<string>): OfflineDocumentList {
  const documents = data.documents ?? [];
  const tasks = new Map((data.tasks ?? []).map((task) => [task.id, task]));
  const pinsByDocument = new Map<string, DocumentPinPayload[]>();
  for (const pin of data.pins ?? []) {
    const task = tasks.get(pin.taskId);
    if (!task) continue;
    pinsByDocument.set(pin.documentId, [...(pinsByDocument.get(pin.documentId) ?? []), toListedPin(pin, task)]);
  }
  const listed = documents.map((document) => toListedDocument(document, pinsByDocument.get(document.id) ?? []));
  return {
    documents: groupVersions(listed),
    totalCount: documents.length,
    saved,
    categories: entities(documents.map((document) => document.category)),
    tags: entities(documents.flatMap((document) => document.tags ?? [])),
    bkpList: data.entities?.bkpList ?? [],
  };
}
